import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { toast } from 'sonner';
import type { Project, CreateProjectCommand, UpdateProjectCommand, DeleteProjectCommand } from '@/types/project';
import { RoleType, ReportGenerationStatus } from '@/types/project';
import { isSeatEntitlementError } from '@/lib/apiErrors';

/** Friendly message shown instead of a generic error toast when the organization's seat pool blocks the action. */
const SEAT_ENTITLEMENT_MESSAGE =
  'Your organization needs more active seats to do this. Ask an owner or editor to buy or activate seats.';

/** How often to poll the project while a report is being generated (ms). */
const REPORT_GENERATION_POLL_INTERVAL_MS = 60_000;
import { invalidateActivityLogDelayed } from '@/hooks/queries/useActivityLogQuery';

// Query keys for cache management
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters?: string) => [...projectKeys.lists(), { filters }] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: number) => [...projectKeys.details(), id] as const,
  active: () => [...projectKeys.all, 'active'] as const,
  myRoles: () => [...projectKeys.all, 'myRoles'] as const,
};

/**
 * Hook to fetch active projects sorted by creation date (descending)
 * GET /api/projects?status=0&sortBy=createdat&sortDirection=desc&page=1&pageSize=20
 */
export const useActiveProjectsQuery = () => {
  return useQuery({
    queryKey: projectKeys.active(),
    queryFn: () => apiClient.getActiveProjects(),
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch all projects with automatic refetching
 */
export const useProjectsQuery = () => {
  return useQuery({
    queryKey: projectKeys.lists(),
    queryFn: () => apiClient.getProjects({ page: 1, pageSize: 1000 }),
    select: (data) => data.items ?? [],
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
};

/**
 * Hook to fetch the current user's role for each project they are a member of.
 * Returns a Record<projectId, RoleType> for use as a lookup.
 */
export const useMyProjectRolesQuery = () => {
  return useQuery({
    queryKey: projectKeys.myRoles(),
    queryFn: async () => {
      const items = await apiClient.getMyProjectRoles();
      return items.reduce<Record<number, RoleType>>((acc, item) => {
        acc[item.projectId] = item.roleType as RoleType;
        return acc;
      }, {});
    },
    gcTime: 5 * 60 * 1000,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Hook to fetch a single project by ID
 */
export const useProjectQuery = (id: number) => {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => apiClient.getProject(id),
    gcTime: 5 * 60 * 1000,
    enabled: !!id, // Only run if id is provided
    // Report generation runs asynchronously on the worker — poll while it's in
    // progress so status/estimate updates show up without a manual reload.
    // Stops automatically once reportGenerationStatus leaves "Generating".
    refetchInterval: (query) =>
      query.state.data?.reportGenerationStatus === ReportGenerationStatus.Generating
        ? REPORT_GENERATION_POLL_INTERVAL_MS
        : false,
  });
};

/**
 * Hook to create a new project
 */
export const useCreateProjectMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (command: CreateProjectCommand) => apiClient.createProject(command),
    onSuccess: (newProject) => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      
      toast.success('Project created successfully');
    },
    onError: (error) => {
      console.error('Failed to create project:', error);
      toast.error(isSeatEntitlementError(error) ? SEAT_ENTITLEMENT_MESSAGE : 'Failed to create project');
    },
  });
};

/**
 * Hook to update an existing project
 */
export const useUpdateProjectMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, command }: { id: number; command: UpdateProjectCommand }) =>
      apiClient.updateProject(id, command),
    onSuccess: (updatedProject, { id }) => {
      // Some backends return 204 No Content for PUT and this client maps that to {}.
      // Only write detail cache when a valid project payload is returned.
      if (updatedProject && typeof updatedProject.id === 'number') {
        queryClient.setQueryData<Project>(projectKeys.detail(id), updatedProject);
      }

      // Always refetch canonical detail and list after update.
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      invalidateActivityLogDelayed(queryClient, id);
      
      toast.success('Project updated successfully');
    },
    onError: (error) => {
      console.error('Failed to update project:', error);
      toast.error(isSeatEntitlementError(error) ? SEAT_ENTITLEMENT_MESSAGE : 'Failed to update project');
    },
  });
};

/**
 * Hook to delete a project
 */
export const useDeleteProjectMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, command }: { id: number; command: DeleteProjectCommand }) =>
      apiClient.deleteProject(id, command),
    onSuccess: (_, { id }) => {
      // Remove detail cache and invalidate list
      queryClient.removeQueries({ queryKey: projectKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      
      toast.success('Project deleted successfully');
    },
    onError: (error) => {
      console.error('Failed to delete project:', error);
      toast.error(isSeatEntitlementError(error) ? SEAT_ENTITLEMENT_MESSAGE : 'Failed to delete project');
    },
  });
};

/**
 * Hook to trigger report generation for a project
 */
export const useGenerateReportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: number) => apiClient.generateReport(projectId),
    onSuccess: (updatedProject, projectId) => {
      queryClient.setQueryData<Project>(projectKeys.detail(projectId), updatedProject);
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      invalidateActivityLogDelayed(queryClient, projectId);
    },
    onError: (error) => {
      console.error('Failed to generate report:', error);
      toast.error(
        isSeatEntitlementError(error)
          ? SEAT_ENTITLEMENT_MESSAGE
          : error instanceof Error
          ? error.message
          : 'Failed to generate report'
      );
    },
  });
};
